import ApiHelper from "../../../common/utils/apiHelper";
// helpers
import { isEmailInvalid } from "../../../common/utils/helpers/isEmailInvalid";
import { checkForEmptyField } from "../../../common/utils/helpers/checkForEmptyFields";
import { extractFullNameParts } from "../../../common/utils/helpers/extractFullNameParts";
import { getFormattedDate } from "../../../common/utils/helpers/getFormattedDate";
import { formatStationWorkingTimeDate } from "./helpers/formatStationWorkingTimeDate";
import { formatStationWorkingTimeTime } from "./helpers/formatStationWorkingTimeTime";
// types
import { StationWorkingTimeParams, StationWorkingTimeResponse } from "./types";
// actions / selectors
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";

export const europcarActionTypes = {
  LOAD_EUROPCAR_INITIAL: "LOAD_EUROPCAR_INITIAL",
  TOGGLE_EUROPCAR_INITIAL_ERROR: "TOGGLE_EUROPCAR_INITIAL_ERROR",
  RESET_EUROPCAR_STATE: "RESET_EUROPCAR_STATE",
  TOGGLE_EUROPCAR_STATION_ERROR: "TOGGLE_EUROPCAR_STATION_ERROR",
  LOAD_EUROPCAR_STATIONS: "LOAD_EUROPCAR_STATIONS",
  LOAD_EUROPCAR_CAR_CATEGORIES: "LOAD_EUROPCAR_CAR_CATEGORIES",
  LOAD_EUROPCAR_COUNTRIES: "LOAD_EUROPCAR_COUNTRIES",
  SET_IS_CAR_CATEGORIES_AVAILABLE: "SET_IS_CAR_CATEGORIES_AVAILABLE",
  SET_DATA_FOR_BOOKING: "SET_DATA_FOR_BOOKING",
  SET_GET_CATEGORIES_ERROR: "SET_GET_CATEGORIES_ERROR",
  SET_EUROPCAR_BOOKING_RESPONSE_DATA: "SET_EUROPCAR_BOOKING_RESPONSE_DATA",
  TOGGLE_EUROPCAR_BOOKING_ERROR: "TOGGLE_EUROPCAR_BOOKING_ERROR",
  TOGGLE_EUROPCAR_PAYMENT_MODAL: "TOGGLE_EUROPCAR_PAYMENT_MODAL",
  SET_BOOKING_ERROR_MESSAGE: "SET_BOOKING_ERROR_MESSAGE",
  SET_STATION_WORKING_TIME: "SET_STATION_WORKING_TIME",
  TOGGLE_STATION_WORKING_TIME_ERROR: "TOGGLE_STATION_WORKING_TIME_ERROR",
  TOGGLE_STATION_WORKING_TIME_LOADING: "TOGGLE_STATION_WORKING_TIME_LOADING"
};

const initialDataForBooking = {
  commentForm: "",
  emailForm: "",
  nameForm: "",
  firstNameForm: "",
  lastNameForm: "",
  mobileNumberForm: "",
  dateOfBirth: new Date("2001-01-01"),
  acceptTerms: false
};

export const initialState = {
  europcarInitial: null,
  europcarInitialItem: null,
  europcarInitialError: false,
  europcarStations: null,
  europcarStationsError: false,
  europcarCarCategories: null,
  europcarCarCategoriesError: false,
  countries: null,
  isCarCategoriesAvailable: false,
  dataForBooking: initialDataForBooking,
  europcarBookingResponseData: null,
  europcarBookingError: false,
  showEuropcarPaymentModal: false,
  bookingErrorMessage: [],
  stationWorkingTime: null as StationWorkingTimeResponse | null,
  stationWorkingTimeError: false,
  stationWorkingTimeLoading: false
};

// Toggle mock support per endpoint (aligned with SBB mock pattern on develop branch)
const USE_MOCK_EUROPCAR_INITIAL = false;
const USE_MOCK_EUROPCAR_NEAREST_STATIONS = false;
const USE_MOCK_EUROPCAR_STATION_WORKING_TIME = false;
const USE_MOCK_EUROPCAR_AVAILABILITY = false;

const MOCK_EUROPCAR_INITIAL = {
  title: "Europcar Car Rental",
  name: "Europcar Car Rental",
  type: "EUROPCAR",
  main: null,
  logo: null,
  provider_cover: {
    url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/a51ac272-4265-45df-b3fa-98033b3c6423/235b81b46df8440-332x118.png",
    name: "EUROPCAR"
  },
  items: [
    {
      id: 1214,
      name: "Europcar",
      type: "EUROPCAR",
      offer_type: null,
      code: "EVRXXAU",
      provider_name: "Europcar",
      provider_logo_id: null,
      provider_cover_id: 24863,
      provider_logo: null,
      departure_address: null,
      destination_address: null,
      airport_pickup: false,
      booking_setting: {
        customer_name: "mandatory",
        customer_email: "mandatory",
        customer_phone: "mandatory",
        comment: "shown",
        birthdate: "shown"
      },
      time_limit: null,
      terms_and_conditions_url: null
    }
  ]
};

const MOCK_EUROPCAR_NEAREST_STATIONS = {
  stations: [
    {
      station_code: "ZRHT01",
      station_name: "Zurich Kloten Airport",
      full_address: "ZURICH AIRPORT, PARKING 3 G1",
      distance_km: 0.0
    },
    {
      station_code: "ZRHC01",
      station_name: "Zurich Main Station",
      full_address: "JOSEFSTRASSE 53",
      distance_km: 1.0
    },
    {
      station_code: "ZRHC16",
      station_name: "Zurich Zentrum Eth Only Eth",
      full_address: "ONLY FOR ETH EMPLOYEES",
      distance_km: 1.0
    },
    {
      station_code: "ZRHW01",
      station_name: "Zurich Altstetten",
      full_address: "FREILAGERSTRASSE 39",
      distance_km: 1.0
    },
    {
      station_code: "ZRHC07",
      station_name: "Zurich Brunaupark",
      full_address: "GIESSHUEBELSTRASSE 65",
      distance_km: 1.0
    }
  ]
};

const MOCK_EUROPCAR_STATION_WORKING_TIME = {
  station_code: "ZRHT01",
  station_exists: true,
  is_open_for_pickup: true,
  is_open_for_return: true,
  is_fully_available: true,
  pickup_schedule: "0630-2330",
  return_schedule: "0630-2330",
  message: "Station is available for both pick-up and return",
  opening_hours_label: "Opening hours",
  opening_hours: [{ days: "Mon - Sunday", hours: "06:30 - 23:30" }],
  exceptions: []
};

const MOCK_EUROPCAR_AVAILABILITY = {
  carCategories: [
    {
      acriss_code: "EDAR",
      sample: "VOLKSWAGEN POLO",
      seats: 5,
      doors: 5,
      transmission: "Automatic",
      fuel_type: "Unleaded",
      image_url: "https://static.europcar.com/carvisuals/partners/835x557/EDAR_CH.png",
      rate_id: "76387e0daf4d7d2503f249811e3d7a853c54b73936b280a1c95bcff6a522d577",
      base_price: 57.6,
      total_estimate: 62.27,
      currency: "CHF",
      status: "F",
      is_bookable: true,
      final_price: 62.27,
      basic_insurance: [
        { code: "WIN", excess: 0.0 },
        { code: "LDW", excess: 1500.0 },
        { code: "LAF", excess: 0.0 }
      ],
      included: [
        { description: "Mileage Unlimited" },
        { description: "Winter equipment" },
        { description: "VAT 8.1 %" }
      ]
    },
    {
      acriss_code: "DDAE",
      sample: "SEAT CUPRA BORN 77KWH RANGE 400KM",
      seats: 4,
      doors: 5,
      transmission: "Automatic",
      fuel_type: "Electric",
      image_url: "https://static.europcar.com/carvisuals/partners/835x557/DDAE_CH.png",
      rate_id: "13ea92b359e6ad05cd9432917a1e531f3c54b73936b280a1c95bcff6a522d577",
      base_price: 57.6,
      total_estimate: 62.27,
      currency: "CHF",
      status: "F",
      is_bookable: true,
      final_price: 62.27,
      basic_insurance: [
        { code: "WIN", excess: 0.0 },
        { code: "LDW", excess: 2500.0 },
        { code: "LAF", excess: 0.0 }
      ],
      included: [{ description: "Mileage Unlimited" }, { description: "VAT 8.1 %" }]
    },
    {
      acriss_code: "CDAR",
      sample: "SKODA SCALA",
      seats: 5,
      doors: 5,
      transmission: "Automatic",
      fuel_type: "Unleaded",
      image_url: "https://static.europcar.com/carvisuals/partners/835x557/CDAR_CH.png",
      rate_id: "60b3e2d53e79b69411952f753644ac713c54b73936b280a1c95bcff6a522d577",
      base_price: 60.0,
      total_estimate: 64.86,
      currency: "CHF",
      status: "F",
      is_bookable: true,
      final_price: 64.86,
      basic_insurance: [
        { code: "WIN", excess: 0.0 },
        { code: "LDW", excess: 3500.0 },
        { code: "LAF", excess: 0.0 }
      ],
      included: [{ description: "VAT 8.1 %" }]
    },
    {
      acriss_code: "CWAR",
      sample: "VOLKSWAGEN GOLF SW",
      seats: 5,
      doors: 5,
      transmission: "Automatic",
      fuel_type: "Unleaded",
      image_url: "https://static.europcar.com/carvisuals/partners/835x557/CWAR_CH.png",
      rate_id: "63a5a690d5bb7e234951e423c4820a553c54b73936b280a1c95bcff6a522d577",
      base_price: 70.8,
      total_estimate: 76.53,
      currency: "CHF",
      status: "F",
      is_bookable: true,
      final_price: 76.53,
      basic_insurance: [
        { code: "WIN", excess: 0.0 },
        { code: "LDW", excess: 4500.0 },
        { code: "LAF", excess: 0.0 }
      ],
      included: [
        { description: "Mileage Unlimited" },
        { description: "Winter equipment" },
        { description: "VAT 8.1 %" },
        { description: "Road Tax & License FES (LAF)" }
      ]
    }
  ],
  session_id: "1234567890"
};

const europcarModule = (state = initialState, action: any) => {
  switch (action.type) {
    case europcarActionTypes.LOAD_EUROPCAR_INITIAL: {
      return {
        ...state,
        europcarInitial: action.europcarInitial,
        europcarInitialItem: action.europcarInitialItem
      };
    }

    case europcarActionTypes.TOGGLE_EUROPCAR_INITIAL_ERROR: {
      return {
        ...state,
        europcarInitialError: action.europcarInitialError
      };
    }

    case europcarActionTypes.LOAD_EUROPCAR_STATIONS: {
      return {
        ...state,
        europcarStations: action.europcarStations
      };
    }

    case europcarActionTypes.LOAD_EUROPCAR_CAR_CATEGORIES: {
      return {
        ...state,
        europcarCarCategories: action.europcarCarCategories,
        europcarSessionId: action.europcarSessionId
      };
    }

    case europcarActionTypes.TOGGLE_EUROPCAR_STATION_ERROR: {
      return {
        ...state,
        europcarStationsError: action.europcarStationsError
      };
    }

    case europcarActionTypes.SET_GET_CATEGORIES_ERROR: {
      return {
        ...state,
        europcarCarCategoriesError: action.europcarCarCategoriesError
      };
    }

    case europcarActionTypes.LOAD_EUROPCAR_COUNTRIES: {
      return {
        ...state,
        countries: action.countries
      };
    }
    case europcarActionTypes.SET_IS_CAR_CATEGORIES_AVAILABLE: {
      return {
        ...state,
        isCarCategoriesAvailable: action.isCarCategoriesAvailable
      };
    }
    case europcarActionTypes.SET_DATA_FOR_BOOKING: {
      return {
        ...state,
        dataForBooking: action.dataForBooking
      };
    }
    case europcarActionTypes.SET_EUROPCAR_BOOKING_RESPONSE_DATA: {
      return {
        ...state,
        europcarBookingResponseData: action.europcarBookingResponseData
      };
    }
    case europcarActionTypes.TOGGLE_EUROPCAR_BOOKING_ERROR: {
      return {
        ...state,
        europcarBookingError: action.europcarBookingError
      };
    }
    case europcarActionTypes.TOGGLE_EUROPCAR_PAYMENT_MODAL: {
      return {
        ...state,
        showEuropcarPaymentModal: action.showEuropcarPaymentModal
      };
    }
    case europcarActionTypes.SET_BOOKING_ERROR_MESSAGE: {
      return {
        ...state,
        bookingErrorMessage: action.bookingErrorMessage
      };
    }
    case europcarActionTypes.SET_STATION_WORKING_TIME: {
      return {
        ...state,
        stationWorkingTime: action.stationWorkingTime
      };
    }
    case europcarActionTypes.TOGGLE_STATION_WORKING_TIME_ERROR: {
      return {
        ...state,
        stationWorkingTimeError: action.stationWorkingTimeError
      };
    }
    case europcarActionTypes.TOGGLE_STATION_WORKING_TIME_LOADING: {
      return {
        ...state,
        stationWorkingTimeLoading: action.stationWorkingTimeLoading
      };
    }

    case europcarActionTypes.RESET_EUROPCAR_STATE: {
      return {
        ...state,
        europcarStations: null,
        europcarStationsError: false,
        europcarCarCategories: null,
        isCarCategoriesAvailable: false,
        dataForBooking: initialDataForBooking,
        europcarBookingResponseData: null,
        europcarBookingError: false,
        showEuropcarPaymentModal: false,
        stationWorkingTime: null,
        stationWorkingTimeError: false
      };
    }

    default:
      return state;
  }
};

export default europcarModule;

export const europcarActionCreators = {
  getEuropcarInitialAction: () => async (dispatch: any, getState: any) => {
    const apiUrl = selectApiBaseUrl(getState());

    // turn on the loader
    dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
    // reset error state
    dispatch({
      type: europcarActionTypes.TOGGLE_EUROPCAR_INITIAL_ERROR,
      europcarInitialError: false
    });

    try {
      const res = USE_MOCK_EUROPCAR_INITIAL
        ? ({ data: MOCK_EUROPCAR_INITIAL } as any)
        : await ApiHelper.get(`${apiUrl}/transport/v1/europcar`);

      dispatch({
        type: europcarActionTypes.LOAD_EUROPCAR_INITIAL,
        europcarInitial: res.data,
        europcarInitialItem: res?.data?.items?.[0]
      });
    } catch (err: any) {
      dispatch({
        type: europcarActionTypes.TOGGLE_EUROPCAR_INITIAL_ERROR,
        europcarInitialError: true
      });
    } finally {
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
    }
  },
  getEuropcarNearestStations: () => async (dispatch: any, getState: any) => {
    const apiUrl = selectApiBaseUrl(getState());

    try {
      const res = USE_MOCK_EUROPCAR_NEAREST_STATIONS
        ? ({ data: MOCK_EUROPCAR_NEAREST_STATIONS } as any)
        : await ApiHelper.get(`${apiUrl}/transport/v1/europcar/nearest-stations`);

      dispatch({
        type: europcarActionTypes.LOAD_EUROPCAR_STATIONS,
        europcarStations: res.data?.stations
      });
    } catch (err: any) {
      dispatch({
        type: europcarActionTypes.TOGGLE_EUROPCAR_STATION_ERROR,
        europcarStationsError: true
      });
    }
  },

  getStationWorkingTimeAction:
    (stationCode: string, params: StationWorkingTimeParams) => async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());

      dispatch({
        type: europcarActionTypes.TOGGLE_STATION_WORKING_TIME_LOADING,
        stationWorkingTimeLoading: true
      });

      dispatch({
        type: europcarActionTypes.TOGGLE_STATION_WORKING_TIME_ERROR,
        stationWorkingTimeError: false
      });

      const apiParams = {
        pickup_date: formatStationWorkingTimeDate(params.pickup_date),
        pickup_time: formatStationWorkingTimeTime(params.pickup_time),
        return_date: formatStationWorkingTimeDate(params.return_date),
        return_time: formatStationWorkingTimeTime(params.return_time)
      };

      try {
        const res = USE_MOCK_EUROPCAR_STATION_WORKING_TIME
          ? ({ data: { ...MOCK_EUROPCAR_STATION_WORKING_TIME, station_code: stationCode } } as any)
          : await ApiHelper.post(
              `${apiUrl}/transport/v1/europcar/station-working-time/${encodeURIComponent(stationCode)}`,
              {},
              apiParams
            );

        dispatch({
          type: europcarActionTypes.SET_STATION_WORKING_TIME,
          stationWorkingTime: res.data
        });
      } catch (err: any) {
        dispatch({
          type: europcarActionTypes.TOGGLE_STATION_WORKING_TIME_ERROR,
          stationWorkingTimeError: true
        });
        dispatch({
          type: europcarActionTypes.SET_STATION_WORKING_TIME,
          stationWorkingTime: null
        });
      } finally {
        dispatch({
          type: europcarActionTypes.TOGGLE_STATION_WORKING_TIME_LOADING,
          stationWorkingTimeLoading: false
        });
      }
    },

  getNationalityCountriesAction: () => async (dispatch: any, getState: any) => {
    const apiUrl = selectApiBaseUrl(getState());

    try {
      const res = await ApiHelper.get(`${apiUrl}/countries`);

      dispatch({
        type: europcarActionTypes.LOAD_EUROPCAR_COUNTRIES,
        countries: res?.data
      });
    } catch (err: any) {}
  },

  setCarCategoriesAvailabilityAction: (isCarCategoriesAvailable: boolean) => (dispatch: any) => {
    dispatch({
      type: europcarActionTypes.SET_IS_CAR_CATEGORIES_AVAILABLE,
      isCarCategoriesAvailable
    });
  },

  getEuropcarCarCategories:
    ({ stationId, pickupDate, pickupTime, returnDate, returnTime, driver_country, driver_age, offerCode }: any) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());

      // reset isCarCategoriesAvailable state
      dispatch(europcarActionCreators.setCarCategoriesAvailabilityAction(false));
      // reset error state
      dispatch({
        type: europcarActionTypes.SET_GET_CATEGORIES_ERROR,
        europcarCarCategoriesError: false
      });

      dispatch(globalActionCreators.toggleGlobalLoaderAction(true));

      const apiParams = {
        pickup_station_id: stationId,
        return_station_id: stationId,
        pickup_date: pickupDate,
        pickup_time: pickupTime,
        return_date: returnDate,
        return_time: returnTime,
        driver_country: driver_country,
        driver_age: Number(driver_age)
      };

      try {
        const res = USE_MOCK_EUROPCAR_AVAILABILITY
          ? ({ data: MOCK_EUROPCAR_AVAILABILITY } as any)
          : await ApiHelper.post(`${apiUrl}/transport/v1/europcar/availability/${offerCode}`, {}, apiParams);

        dispatch({
          type: europcarActionTypes.LOAD_EUROPCAR_CAR_CATEGORIES,
          europcarCarCategories: res.data?.carCategories,
          europcarSessionId: res.data?.session_id
        });

        dispatch(europcarActionCreators.setCarCategoriesAvailabilityAction(true));
      } catch (err: any) {
        dispatch(europcarActionCreators.setCarCategoriesAvailabilityAction(false));
        dispatch({
          type: europcarActionTypes.SET_GET_CATEGORIES_ERROR,
          europcarCarCategoriesError: true
        });
      } finally {
        dispatch(globalActionCreators.toggleGlobalLoaderAction(false));
      }
    },

  updateFormForBookingAction:
    (dataForBooking: {
      commentForm: string;
      emailForm: string;
      nameForm: string;
      mobileNumberForm: string;
      dateOfBirth: Date;
      acceptTerms?: boolean;
    }) =>
    (dispatch: any) => {
      dispatch({
        type: europcarActionTypes.SET_DATA_FOR_BOOKING,
        dataForBooking: dataForBooking
      });
    },

  buyTicketAction: (params: any, navigate: any) => async (dispatch: any, getState: any) => {
    const dataForBooking = getState().europcar.dataForBooking;
    const europcarInitialItem = getState().europcar.europcarInitialItem;

    dispatch(europcarActionCreators?.toggleEuropcarBookingErrorAction(false));

    let errorField = {
      emailForm: false,
      commentForm: false,
      nameForm: false,
      firstNameForm: false,
      lastNameForm: false,
      mobileNumberForm: false,
      acceptTerms: false
    };

    let errorCheck = true;

    if (
      isEmailInvalid(dataForBooking.emailForm) ||
      (checkForEmptyField(dataForBooking.emailForm) &&
        europcarInitialItem?.booking_setting?.customer_email === "mandatory")
    ) {
      errorField.emailForm = true;
      errorCheck = false;
    }

    if (
      (europcarInitialItem?.booking_setting?.comment === "mandatory" &&
        checkForEmptyField(dataForBooking.commentForm)) ||
      (dataForBooking?.commentForm !== "" && dataForBooking?.commentForm?.length < 3)
    ) {
      errorField.commentForm = true;
      errorCheck = false;
    }

    if (
      europcarInitialItem?.booking_setting?.customer_name === "mandatory" &&
      checkForEmptyField(dataForBooking.nameForm)
    ) {
      errorField.nameForm = true;
      errorCheck = false;
    }

    if (
      europcarInitialItem?.booking_setting?.first_name === "mandatory" &&
      checkForEmptyField(dataForBooking.firstNameForm)
    ) {
      errorField.firstNameForm = true;
      errorCheck = false;
    }

    if (
      europcarInitialItem?.booking_setting?.last_name === "mandatory" &&
      checkForEmptyField(dataForBooking.lastNameForm)
    ) {
      errorField.lastNameForm = true;
      errorCheck = false;
    }

    if (
      europcarInitialItem?.booking_setting?.customer_phone === "mandatory" &&
      checkForEmptyField(dataForBooking.mobileNumberForm)
    ) {
      errorField.mobileNumberForm = true;
      errorCheck = false;
    }

    if (europcarInitialItem?.terms_and_conditions_url && !dataForBooking.acceptTerms) {
      errorField.acceptTerms = true;
      errorCheck = false;
    }

    if (!errorCheck) {
      dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: true, errorField: errorField });
    } else {
      dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: false, errorField: errorField });
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      // reset booking error state
      dispatch(europcarActionCreators?.toggleEuropcarBookingErrorAction(false));

      // reset booking error message
      dispatch(europcarActionCreators.setErrorMessageAction([]));

      // remove bookingResponseDataStorage from the storage, before setting it again
      localStorage.removeItem("bookingResponseDataStorage");

      const apiUrl = selectApiBaseUrl(getState());
      const language = localStorage.getItem("i18nextLng");

      const selectedCar = params?.selectedCar;

      const apiParams = {
        summary: {
          start_date: params?.start_date,
          adult: 1,
          timeslot: params?.timeslot,
          offer_code: europcarInitialItem?.code
        },
        personal_information: {
          first_name: dataForBooking.firstNameForm?.trim() || extractFullNameParts(dataForBooking.nameForm)?.firstName,
          last_name: dataForBooking.lastNameForm?.trim() || extractFullNameParts(dataForBooking.nameForm)?.lastName,
          email: dataForBooking.emailForm,
          phone: dataForBooking.mobileNumberForm,
          birthdate: dataForBooking?.dateOfBirth ? getFormattedDate(dataForBooking.dateOfBirth) : null,
          comment: dataForBooking.commentForm ? dataForBooking.commentForm : null
        },
        hotel_stay_code: getState().tenant.tenantInfo?.code,
        extraBookingData: {
          countryOfResidence: params?.countryOfResidence,
          sendBookingConfirmationCopy: true,
          car_category: selectedCar?.acriss_code,
          rate_id: selectedCar?.rate_id,
          pickup_station_id: params?.pickup_station_id,
          return_station_id: params?.return_station_id,
          pickup_date: params?.pickupDate,
          pickup_time: params?.pickupTime,
          return_date: params?.returnDate,
          return_time: params?.returnTime
        },
        session_id: getState().europcar.europcarSessionId
      };

      try {
        const response = await ApiHelper.post(`${apiUrl}/bookings`, {}, apiParams);

        localStorage.setItem("bookingResponseDataStorage", JSON.stringify(response.data));

        // We can get API status 200, with booking_status=error --> in that case we should show error page instead of Payment Modal / Confirmation Page
        if (response.data?.booking_status === "error") {
          dispatch(europcarActionCreators?.toggleEuropcarBookingErrorAction(true));
          return;
        } else {
          dispatch(europcarActionCreators?.toggleEuropcarBookingErrorAction(false));

          dispatch({
            type: europcarActionTypes.SET_EUROPCAR_BOOKING_RESPONSE_DATA,
            europcarBookingResponseData: response.data
          });

          // If price is 0 we will get payment_status=paid from API --> and we should redirect user directly to the /confirmation page (without payment modal)
          if (response.data?.payment_status === "paid") {
            // call booking status action, in order to set new value to the bookingStatus state
            await dispatch(confirmationActionCreators?.getBookingStatusAction());
            // redirect to confirmation
            await navigate(`/${language}/transport/europcar/confirmation`, { replace: true });
          } else {
            // open payment modal (if status is not booked)
            dispatch(europcarActionCreators.toggleEuropcarPaymentModalAction(true));
          }
        }
      } catch (err: any) {
        if (err?.response?.data?.fields) {
          dispatch(europcarActionCreators.setErrorMessageAction(err?.response?.data?.fields));
        }

        dispatch(europcarActionCreators?.toggleEuropcarBookingErrorAction(true));
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    }
  },

  toggleEuropcarBookingErrorAction: (europcarBookingError: boolean) => (dispatch: any) => {
    dispatch({
      type: europcarActionTypes.TOGGLE_EUROPCAR_BOOKING_ERROR,
      europcarBookingError
    });
  },

  toggleEuropcarPaymentModalAction: (showEuropcarPaymentModal: boolean) => (dispatch: any) => {
    dispatch({
      type: europcarActionTypes.TOGGLE_EUROPCAR_PAYMENT_MODAL,
      showEuropcarPaymentModal
    });
  },
  setErrorMessageAction: (message: any) => (dispatch: any) => {
    dispatch({
      type: europcarActionTypes.SET_BOOKING_ERROR_MESSAGE,
      bookingErrorMessage: message
    });
  },

  resetEuropcarState: () => (dispatch: any) => {
    dispatch({
      type: europcarActionTypes.RESET_EUROPCAR_STATE
    });
  }
};
